import React, { useContext, useEffect, useState } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import axios from "axios";

const AuthContext = React.createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      if (user) {
        await checkAuth(user);
      } else {
        setCurrentUser(null);
        setUserRole(null);
        setLoading(false);
      }
    });
    return () => {
      unsub();
    };
  }, []);

  const checkAuth = async user => {
    try {
      if (user) {
        user
          .getIdToken()
          .then(token => {
            axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          })
          .catch(error => {
            console.error("Error fetching token", error);
          });
      }
      const tokenResult = await user.getIdTokenResult(true); // Force refresh to get latest claims
      const claims = tokenResult.claims;

      if (
        claims.admin ||
        claims.subadmin ||
        claims.teacher ||
        claims.optician
      ) {
        setCurrentUser(user);

        if (claims.admin) {
          setUserRole("admin");
        } else if (claims.subadmin) {
          setUserRole("sub-admin");
        } else if (claims.optician) {
          setUserRole("optician");
        } else if (claims.teacher) {
          const role = claims.role;
          switch (role) {
            case "special":
              setUserRole("specialTeacher");
              break;
            case "principal":
              setUserRole("principalTeacher");
              break;
            case "class":
              setUserRole("classTeacher");
              break;
            default:
              setUserRole("teacher"); // Fallback for undefined roles
          }
        }
      } else {
        setError("You do not have the necessary permissions.");
        await signOut(auth);
        setCurrentUser(null);
        setUserRole(null);
      }
    } catch (err) {
      console.error("Error fetching user claims:", err);
      setError("An error occurred during authentication.");
      await signOut(auth);
      setCurrentUser(null);
      setUserRole(null);
    } finally {
      setLoading(false);
    }
  };

  const values = {
    currentUser,
    setCurrentUser,
    userRole,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={values}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
